import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Loader2, AlertCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminDataApi } from '../../../api/adminDataApi';
import { format } from 'date-fns';

interface DataExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    businessId: string;
    businessName: string;
}

const DataExportModal: React.FC<DataExportModalProps> = ({ isOpen, onClose, businessId, businessName }) => {
    const queryClient = useQueryClient();
    const [selectedType, setSelectedType] = useState<string>('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [formatType, setFormatType] = useState('csv');
    const [sourceType, setSourceType] = useState<'db' | 'live'>('db');

    // Fetch Export Types
    const { data: exportTypes, isLoading: isLoadingTypes } = useQuery({
        queryKey: ['exportTypes'],
        queryFn: adminDataApi.listExportTypes,
        enabled: isOpen
    });

    // Fetch Recent Jobs
    const { data: jobs, isLoading: isLoadingJobs } = useQuery({
        queryKey: ['exportJobs', businessId],
        queryFn: () => adminDataApi.listExportJobs(businessId),
        enabled: isOpen,
        refetchInterval: (query) => {
            // Poll if any job is running
            const hasRunning = query.state.data?.some(j => j.status === 'queued' || j.status === 'running');
            return hasRunning ? 3000 : false;
        }
    });

    // Default to first type
    useEffect(() => {
        if (exportTypes && exportTypes.length > 0 && !selectedType) {
            setSelectedType(exportTypes[0].id);
        }
    }, [exportTypes]);

    // Reset inputs when type changes
    useEffect(() => {
        setStartDate('');
        setEndDate('');
        setSelectedMonth('');
    }, [selectedType]);

    const isQboType = selectedType.startsWith('qbo_');

    // Generate Mutation
    const generateMutation = useMutation({
        mutationFn: adminDataApi.generateExport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['exportJobs', businessId] });
        }
    });

    const handleGenerate = () => {
        if (!selectedType) return;

        let finalStartDate = startDate;
        let finalEndDate = endDate;

        // If Margin Weekly, derive dates from selected month
        if (selectedType === 'margin_weekly' && selectedMonth) {
            const [year, month] = selectedMonth.split('-');
            const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
            const lastDay = new Date(parseInt(year), parseInt(month), 0); // Last day of month

            finalStartDate = format(firstDay, 'yyyy-MM-dd');
            finalEndDate = format(lastDay, 'yyyy-MM-dd');
        }

        // If Cashflow, clear dates (optional, but good for cleanliness)
        if (selectedType === 'cashflow_forecast') {
            finalStartDate = '';
            finalEndDate = '';
        }

        generateMutation.mutate({
            type: selectedType,
            business_id: businessId,
            start_date: finalStartDate || undefined,
            end_date: finalEndDate || undefined,
            format: formatType,
            source: isQboType ? sourceType : 'db',
        });
    };

    const handleDownload = async (jobId: string) => {
        try {
            const { url } = await adminDataApi.getDownloadUrl(jobId);
            window.open(url, '_blank');
        } catch (error) {
            console.error("Download failed:", error);
            alert("Failed to get download link");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[70] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose}>
                            <span className="sr-only">Close</span>
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="sm:flex sm:items-start w-full">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Export Data for {businessName}
                            </h3>

                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Configuration Panel */}
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Export Type</label>
                                        <select
                                            value={selectedType}
                                            onChange={(e) => setSelectedType(e.target.value)}
                                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                        >
                                            {exportTypes?.map((type) => (
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {exportTypes?.find(t => t.id === selectedType)?.description}
                                        </p>
                                    </div>

                                    {/* Date Inputs Conditional Rendering */}
                                    {selectedType === 'margin_weekly' ? (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Select Month</label>
                                            <input
                                                type="month"
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(e.target.value)}
                                                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                    ) : selectedType === 'cashflow_forecast' ? (
                                        <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                                            <p className="text-sm text-blue-700">
                                                Cashflow forecast automatically generates a 13-week projection starting from the current week. No date selection required.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Start Date (Optional)</label>
                                                <input
                                                    type="date"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">End Date (Optional)</label>
                                                <input
                                                    type="date"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Format</label>
                                        <div className="mt-2 flex items-center space-x-4">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    value="csv"
                                                    checked={formatType === 'csv'}
                                                    onChange={(e) => setFormatType(e.target.value)}
                                                    className="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">CSV</span>
                                            </label>
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    value="json"
                                                    checked={formatType === 'json'}
                                                    onChange={(e) => setFormatType(e.target.value)}
                                                    className="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">JSON</span>
                                            </label>
                                        </div>
                                    </div>

                                    {isQboType && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Source</label>
                                            <div className="mt-2 flex items-center space-x-4">
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        value="db"
                                                        checked={sourceType === 'db'}
                                                        onChange={() => setSourceType('db')}
                                                        className="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">DB</span>
                                                </label>
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        value="live"
                                                        checked={sourceType === 'live'}
                                                        onChange={() => setSourceType('live')}
                                                        className="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">Live QBO</span>
                                                </label>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Live QBO will fetch directly from QuickBooks and also trigger ingest into Mongo using the existing pipeline when supported.
                                            </p>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleGenerate}
                                        disabled={generateMutation.isPending || isLoadingTypes}
                                        className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                    >
                                        {generateMutation.isPending ? (
                                            <>
                                                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <FileText className="-ml-1 mr-2 h-4 w-4" />
                                                Generate Export
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* History Panel */}
                                <div className="border-l pl-8 flex flex-col h-[400px]">
                                    <h4 className="text-sm font-medium text-gray-900 mb-4">Recent Exports</h4>
                                    <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                                        {isLoadingJobs ? (
                                            <div className="flex justify-center py-4">
                                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                                            </div>
                                        ) : jobs && jobs.length > 0 ? (
                                            jobs.map((job) => (
                                                <div key={job.job_id} className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-sm font-medium text-gray-900 capitalize">
                                                                    {job.type.replace('_', ' ')}
                                                                </p>
                                                                {job.format && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 uppercase">
                                                                        {job.format}
                                                                    </span>
                                                                )}
                                                                {job.source && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 uppercase">
                                                                        {job.source}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500">
                                                                {format(new Date(job.created_at), 'MMM d, h:mm a')}
                                                            </p>
                                                        </div>
                                                        {job.status === 'ready' ? (
                                                            <button
                                                                onClick={() => handleDownload(job.job_id)}
                                                                className="text-indigo-600 hover:text-indigo-800"
                                                                title="Download"
                                                            >
                                                                <Download className="h-4 w-4" />
                                                            </button>
                                                        ) : job.status === 'failed' ? (
                                                            <span title={job.error}>
                                                                <AlertCircle className="h-4 w-4 text-red-500" />
                                                            </span>
                                                        ) : (
                                                            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                                        )}
                                                    </div>
                                                    {job.status === 'ready' && (
                                                        <div className="mt-2 flex items-center text-xs text-gray-500 gap-3">
                                                            <span>{(job.file_size || 0) / 1024 > 1024 ? `${((job.file_size || 0) / 1024 / 1024).toFixed(1)} MB` : `${((job.file_size || 0) / 1024).toFixed(1)} KB`}</span>
                                                            <span>{job.row_count} rows</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500 text-center py-4">No recent exports.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataExportModal;
